import { useEffect, useRef, useState } from 'react';

export interface CarouselImage { src: string; alt?: string; /** A small pill on the picture ("Bedroom 2"). */ label?: string }

/**
 * Pictures in one frame that moves on by itself every few seconds and loops (the listing page's gallery, the
 * listing-form card). One picture: just the picture. Hovering or focusing pauses it; a swipe moves it on a phone; the
 * dots jump to a picture; reduced motion stops the auto step (the dots and swipes still work). `onOpen` makes each
 * picture a button (the full-screen pop-up). The frame is 4:3 unless `className` says otherwise.
 */
export function ImageCarousel({ images, alt = '', className = '', every = 4000, onOpen }: {
  images: Array<string | CarouselImage>; alt?: string; className?: string; every?: number; onOpen?: (index: number) => void;
}) {
  const items: CarouselImage[] = images.map((x) => (typeof x === 'string' ? { src: x } : x));
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);
  const n = items.length;
  useEffect(() => { setI(0); }, [n]);
  useEffect(() => {
    if (n < 2 || paused) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % n), every);
    return () => window.clearInterval(t);
  }, [n, paused, every]);
  const step = (d: number) => setI((x) => (x + d + n) % n);

  const picture = (it: CarouselImage, k: number) => {
    const img = <img src={it.src} alt={it.alt ?? (k === 0 ? alt : '')} className="h-full w-full object-cover" loading={k === 0 ? 'eager' : 'lazy'} draggable={false} />;
    return onOpen
      ? <button type="button" onClick={() => onOpen(k)} aria-label={`Open ${it.label ?? `picture ${k + 1}`} full screen`} className="block h-full w-full cursor-zoom-in">{img}</button>
      : img;
  };

  if (n < 2) return <div className={`relative aspect-[4/3] w-full overflow-hidden ${className}`}>{items[0] && picture(items[0], 0)}{items[0]?.label && <Label>{items[0].label}</Label>}</div>;
  return (
    <div
      className={`relative aspect-[4/3] w-full overflow-hidden ${className}`}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; setPaused(true); }}
      onTouchEnd={(e) => { const x0 = touchX.current; touchX.current = null; setPaused(false); if (x0 == null) return; const dx = (e.changedTouches[0]?.clientX ?? x0) - x0; if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1); }}
    >
      <div className="flex h-full transition-transform duration-700 [transition-timing-function:cubic-bezier(.22,1,.36,1)]" style={{ transform: `translateX(-${i * 100}%)` }}>
        {items.map((it, k) => <div key={it.src + k} className="relative h-full w-full shrink-0">{picture(it, k)}</div>)}
      </div>
      {items[i]?.label && <Label>{items[i].label}</Label>}
      <div className="absolute inset-x-0 bottom-3 flex justify-center gap-1.5" role="tablist" aria-label="Pictures">
        {items.map((_, k) => (
          <button key={k} type="button" role="tab" aria-selected={k === i} aria-label={`Picture ${k + 1} of ${n}`} onClick={() => setI(k)}
            className={`h-1.5 rounded-full transition-all duration-300 ${k === i ? 'w-5 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/90'}`} />
        ))}
      </div>
    </div>
  );
}

function Label({ children }: { children: string }) {
  return <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-ink-900/70 px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] text-white">{children}</span>;
}
